import { type LucideIcon, Search, Receipt, Wallet, UserPlus, FileQuestion, History } from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { fadeInUp, tapScale } from '@/lib/motion'

export type EmptyStateVariant =
  | 'no-rooms'
  | 'no-transactions'
  | 'empty-wallet'
  | 'new-agent'
  | 'no-results'
  | 'no-history'

interface VariantConfig {
  icon: LucideIcon
  title: string
  description: string
}

const variantDefaults: Record<EmptyStateVariant, VariantConfig> = {
  'no-rooms': {
    icon: Search,
    title: '尚無房間',
    description: '目前沒有可用的房間，您可以創建一個新房間或稍後再回來查看。',
  },
  'no-transactions': {
    icon: Receipt,
    title: '尚無交易記錄',
    description: '您還沒有任何交易記錄。完成首次儲值或遊戲後會在此顯示。',
  },
  'empty-wallet': {
    icon: Wallet,
    title: '錢包為空',
    description: '您的錢包目前沒有餘額。請先進行儲值以開始遊戲。',
  },
  'new-agent': {
    icon: UserPlus,
    title: '歡迎成為代理',
    description: '您尚未創建任何房間。創建您的第一個私人房間開始賺取佣金！',
  },
  'no-results': {
    icon: FileQuestion,
    title: '找不到結果',
    description: '沒有符合您搜尋條件的結果。請嘗試調整篩選條件。',
  },
  'no-history': {
    icon: History,
    title: '尚無歷史記錄',
    description: '您還沒有任何遊戲歷史記錄。開始一場遊戲後會在此顯示。',
  },
}

export interface EmptyStateProps {
  /** Lucide icon component to display */
  icon?: LucideIcon
  /** Main heading (Chinese) */
  title?: string
  /** Secondary descriptive text */
  description?: string
  /** Optional action button */
  action?: {
    label: string
    onClick: () => void
  }
  /** Pre-built variant with default icon/title/description */
  variant?: EmptyStateVariant
  /** Additional class name */
  className?: string
}

export function EmptyState({
  icon: iconProp,
  title: titleProp,
  description: descriptionProp,
  action,
  variant,
  className,
}: EmptyStateProps) {
  const defaults = variant ? variantDefaults[variant] : undefined
  const Icon = iconProp ?? defaults?.icon ?? FileQuestion
  const title = titleProp ?? defaults?.title ?? '尚無資料'
  const description = descriptionProp ?? defaults?.description ?? ''

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
      className={cn(
        'flex flex-col items-center justify-center py-16 px-6 text-center',
        className
      )}
    >
      {/* Icon container */}
      <div className="w-20 h-20 rounded-3xl bg-surface-elevated border border-foreground/5 flex items-center justify-center mb-6">
        <Icon className="w-10 h-10 text-muted-foreground/50" />
      </div>

      {/* Title */}
      <h4 className="text-lg font-semibold text-foreground mb-2">{title}</h4>

      {/* Description */}
      {description && (
        <p className="text-sm text-muted-foreground leading-relaxed max-w-[280px]">
          {description}
        </p>
      )}

      {/* Action button */}
      {action && (
        <motion.button
          whileTap={tapScale}
          onClick={action.onClick}
          className="mt-6 h-11 px-6 rounded-xl btn-primary font-semibold text-sm"
        >
          {action.label}
        </motion.button>
      )}
    </motion.div>
  )
}
